import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, MessageSquare, BarChart3, Pencil, Trash2, Pause, Play } from 'lucide-react';
import { getSurveys, createSurvey, updateSurvey, deleteSurvey } from '../api/client';
import SurveyBuilder from '../components/surveys/SurveyBuilder';
import SurveyResponses from '../components/surveys/SurveyResponses';
import { formatDate } from '../utils/formatters';
import clsx from 'clsx';
import type { Survey } from '../api/client';

export default function SurveysPage() {
  const queryClient = useQueryClient();
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState<Survey | null>(null);
  const [viewingResponsesId, setViewingResponsesId] = useState<string | null>(null);

  const { data: surveys = [], isLoading } = useQuery({
    queryKey: ['surveys'],
    queryFn: getSurveys,
  });

  const createMutation = useMutation({
    mutationFn: createSurvey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      setShowBuilder(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Survey> }) => updateSurvey(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      setEditingSurvey(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSurvey,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['surveys'] }),
  });

  function getStatusBadge(status: Survey['status']) {
    const styles: Record<string, string> = {
      active: 'badge-success',
      draft: 'badge bg-gray-100 text-gray-800',
      paused: 'badge-warning',
      completed: 'badge-info',
    };
    return <span className={styles[status] || styles.draft}>{status}</span>;
  }

  if (viewingResponsesId) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => setViewingResponsesId(null)}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          &larr; Back to Surveys
        </button>
        <SurveyResponses surveyId={viewingResponsesId} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Surveys</h1>
        <button onClick={() => setShowBuilder(true)} className="btn-primary gap-2">
          <Plus className="h-4 w-4" />
          Create Survey
        </button>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
        </div>
      ) : surveys.length === 0 ? (
        <div className="card flex h-64 items-center justify-center">
          <div className="text-center">
            <MessageSquare className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-2 text-lg font-medium text-gray-500">No surveys yet</p>
            <p className="mt-1 text-sm text-gray-400">
              Create your first survey to collect user feedback
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {surveys.map((survey) => (
            <div key={survey.id} className="card">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{survey.name}</h3>
                  <p className="mt-1 text-xs text-gray-500">
                    {(survey.questions || []).length} questions &middot; Created{' '}
                    {formatDate(survey.createdAt || '')}
                  </p>
                </div>
                {getStatusBadge(survey.status)}
              </div>

              <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
                <span className="text-sm text-gray-500">
                  {survey.responseCount || 0} responses
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setViewingResponsesId(survey.id)}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title="View responses"
                  >
                    <BarChart3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() =>
                      updateMutation.mutate({
                        id: survey.id,
                        data: { status: survey.status === 'active' ? 'paused' : 'active' },
                      })
                    }
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title={survey.status === 'active' ? 'Pause' : 'Activate'}
                  >
                    {survey.status === 'active' ? (
                      <Pause className="h-4 w-4" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => setEditingSurvey(survey)}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate(survey.id)}
                    className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showBuilder || editingSurvey) && (
        <SurveyBuilder
          survey={editingSurvey}
          onClose={() => {
            setShowBuilder(false);
            setEditingSurvey(null);
          }}
          onSave={(data) => {
            if (editingSurvey) {
              updateMutation.mutate({ id: editingSurvey.id, data });
            } else {
              createMutation.mutate(data as Parameters<typeof createSurvey>[0]);
            }
          }}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}
